import { Box, Typography, useTheme, alpha } from "@mui/material";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { ACCENT } from "./accent";

const THRUST = ACCENT; // blue
const CURRENT = "#e0a11b"; // amber
const POWER = "#e5484d"; // red
const EFF = "#2e9d5a"; // green

const axisText = (theme) => ({ fontSize: 11, fill: theme.palette.text.secondary });

const Chart = ({ title, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: "1 1 380px",
        minWidth: 300,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "dark" ? 0.08 : 0.6),
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
        {title}
      </Typography>
      <Box sx={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </Box>
    </Box>
  );
};

/**
 * eCalc-style "Motor Characteristic" charts from a modeled throttle sweep.
 * `curve` rows: { throttle_pct, thrust_g, total_current_a, power_w, efficiency_pct }.
 */
const PropulsionCharts = ({ curve, note = "Modeled characteristic (±~15%)" }) => {
  const theme = useTheme();
  if (!curve?.length) return null;

  const grid = alpha(theme.palette.divider, 0.5);
  const tooltipStyle = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    fontSize: 12,
  };
  const xAxis = (
    <XAxis dataKey="throttle_pct" type="number" domain={[5, 100]} tickCount={6} tick={axisText(theme)} unit="%" stroke={grid} />
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap" }}>
        <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, letterSpacing: 0.4 }}>
          MOTOR CHARACTERISTIC
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {note}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Chart title="Thrust & current vs throttle">
          <LineChart data={curve} margin={{ top: 6, right: 8, bottom: 4, left: -8 }}>
            <CartesianGrid stroke={grid} strokeDasharray="3 3" />
            {xAxis}
            <YAxis yAxisId="l" tick={axisText(theme)} stroke={grid} width={44} />
            <YAxis yAxisId="r" orientation="right" tick={axisText(theme)} stroke={grid} width={36} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line yAxisId="l" type="monotone" dataKey="thrust_g" name="Total thrust (g)" stroke={THRUST} dot={false} strokeWidth={2} />
            <Line yAxisId="r" type="monotone" dataKey="total_current_a" name="Current (A)" stroke={CURRENT} dot={false} strokeWidth={2} />
          </LineChart>
        </Chart>

        <Chart title="Power & efficiency vs throttle">
          <LineChart data={curve} margin={{ top: 6, right: 8, bottom: 4, left: -8 }}>
            <CartesianGrid stroke={grid} strokeDasharray="3 3" />
            {xAxis}
            <YAxis yAxisId="l" tick={axisText(theme)} stroke={grid} width={44} />
            <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={axisText(theme)} stroke={grid} width={36} unit="%" />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line yAxisId="l" type="monotone" dataKey="power_w" name="System power (W)" stroke={POWER} dot={false} strokeWidth={2} />
            <Line yAxisId="r" type="monotone" dataKey="efficiency_pct" name="Motor eff. (%)" stroke={EFF} dot={false} strokeWidth={2} />
          </LineChart>
        </Chart>
      </Box>
    </Box>
  );
};

export default PropulsionCharts;
